import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

type MutOpts<TVars> = UseMutationOptions<unknown, Error, TVars>;
import estimationService, { EstimationRequest, MarginRequest, ItemFeatures } from '../services/estimation.service';

// Query Keys
export const estimationKeys = {
  all: ['estimations'] as const,
  lists: () => [...estimationKeys.all, 'list'] as const,
  list: (filters: any) => [...estimationKeys.lists(), filters] as const,
  details: () => [...estimationKeys.all, 'detail'] as const,
  detail: (id: string) => [...estimationKeys.details(), id] as const,
  versions: (id: string) => [...estimationKeys.all, 'versions', id] as const,
  actuals: (id: string) => [...estimationKeys.all, 'actuals', id] as const,
  trends: (itemId: string) => [...estimationKeys.all, 'trends', itemId] as const,
  accuracy: ['accuracy'] as const,
  marginRules: ['margin-rules'] as const,
  mlAccuracy: ['ml-accuracy'] as const,
  mlHistory: ['ml-history'] as const,
  resourceForecast: ['resource-forecast'] as const
};

// Estimation CRUD Hooks

export const useEstimations = (filters?: any, options?: UseQueryOptions<unknown, Error, unknown>) => {
  return useQuery({
    queryKey: estimationKeys.list(filters),
    queryFn: () => estimationService.getAll(filters),
    ...options
  });
};

export const useEstimation = (id: string, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.detail(id),
    queryFn: () => estimationService.getById(id),
    enabled: !!id,
    ...options
  });
};

export const useCreateEstimation = (options?: MutOpts<EstimationRequest>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EstimationRequest) => estimationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.lists() });
    },
    ...options
  });
};

export const useUpdateEstimation = (options?: MutOpts<{ id: string; data: Partial<EstimationRequest> }>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EstimationRequest> }) =>
      estimationService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: estimationKeys.lists() });
    },
    ...options
  });
};

export const useDeleteEstimation = (options?: MutOpts<string>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => estimationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.lists() });
    },
    ...options
  });
};

// Cost Calculation Hooks

export const useCalculateCost = (options?: MutOpts<{ bomId: string; quantity: number }>) => {
  return useMutation({
    mutationFn: ({ bomId, quantity }: { bomId: string; quantity: number }) =>
      estimationService.calculateCost(bomId, quantity),
    ...options
  });
};

export const useRecalculateEstimation = (options?: MutOpts<string>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => estimationService.recalculate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.detail(id) });
    },
    ...options
  });
};

export const useMakeVsBuyAnalysis = (options?: MutOpts<{ itemId: string; quantity: number }>) => {
  return useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: string; quantity: number }) =>
      estimationService.makeVsBuyAnalysis(itemId, quantity),
    ...options
  });
};

// Version Management Hooks

export const useVersions = (estimationId: string, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.versions(estimationId),
    queryFn: () => estimationService.getVersions(estimationId),
    enabled: !!estimationId,
    ...options
  });
};

export const useCreateVersion = (options?: MutOpts<{ estimationId: string; data: unknown }>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ estimationId, data }: { estimationId: string; data: any }) =>
      estimationService.createVersion(estimationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.versions(variables.estimationId) });
    },
    ...options
  });
};

export const useCompareVersions = (options?: MutOpts<{ version1Id: string; version2Id: string }>) => {
  return useMutation({
    mutationFn: ({ version1Id, version2Id }: { version1Id: string; version2Id: string }) =>
      estimationService.compareVersions(version1Id, version2Id),
    ...options
  });
};

export const useCreateWhatIfScenario = (
  options?: MutOpts<{ estimationId: string; scenarioName: string; changes: unknown[] }>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ estimationId, scenarioName, changes }: { estimationId: string; scenarioName: string; changes: any[] }) =>
      estimationService.createWhatIfScenario(estimationId, scenarioName, changes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.versions(variables.estimationId) });
    },
    ...options
  });
};

// Historical Analysis Hooks

export const useActualCosts = (estimationId: string, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.actuals(estimationId),
    queryFn: () => estimationService.getActualCosts(estimationId),
    enabled: !!estimationId,
    ...options
  });
};

export const useRecordActualCosts = (options?: MutOpts<{ estimationId: string; data: unknown }>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ estimationId, data }: { estimationId: string; data: any }) =>
      estimationService.recordActualCosts(estimationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.actuals(variables.estimationId) });
    },
    ...options
  });
};

export const useVarianceAnalysis = (estimationId: string, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: ['variance-analysis', estimationId],
    queryFn: () => estimationService.getVarianceAnalysis(estimationId),
    enabled: !!estimationId,
    ...options
  });
};

export const useCostTrends = (itemId: string, periodMonths: number = 12, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.trends(itemId),
    queryFn: () => estimationService.getCostTrends(itemId, periodMonths),
    enabled: !!itemId,
    ...options
  });
};

export const useAccuracyMetrics = (estimatorId?: string, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.accuracy,
    queryFn: () => estimationService.getAccuracyMetrics(estimatorId),
    ...options
  });
};

// Margin Optimization Hooks

export const useCalculateOptimalMargin = (
  options?: MutOpts<{ estimationId: string; request: MarginRequest }>
) => {
  return useMutation({
    mutationFn: ({ estimationId, request }: { estimationId: string; request: MarginRequest }) =>
      estimationService.calculateOptimalMargin(estimationId, request),
    ...options
  });
};

export const useMarginScenarios = (totalCost: number, targetRevenue?: number, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: ['margin-scenarios', totalCost, targetRevenue],
    queryFn: () => estimationService.getMarginScenarios(totalCost, targetRevenue),
    enabled: !!totalCost,
    ...options
  });
};

export const useMarginRules = (isActive?: boolean, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.marginRules,
    queryFn: () => estimationService.getMarginRules(isActive),
    ...options
  });
};

export const useCreateMarginRule = (options?: MutOpts<unknown>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => estimationService.createMarginRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.marginRules });
    },
    ...options
  });
};

export const useUpdateMarginRule = (options?: MutOpts<{ id: string; data: unknown }>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      estimationService.updateMarginRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.marginRules });
    },
    ...options
  });
};

export const useDeleteMarginRule = (options?: MutOpts<string>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => estimationService.deleteMarginRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.marginRules });
    },
    ...options
  });
};

// AI/ML Prediction Hooks

export const usePredictCost = (options?: MutOpts<ItemFeatures>) => {
  return useMutation({
    mutationFn: (features: ItemFeatures) => estimationService.predictCost(features),
    ...options
  });
};

export const useTrainModel = (options?: MutOpts<void>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => estimationService.trainModel(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: estimationKeys.mlAccuracy });
    },
    ...options
  });
};

export const useModelAccuracy = (options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.mlAccuracy,
    queryFn: () => estimationService.getModelAccuracy(),
    ...options
  });
};

export const usePredictionHistory = (limit: number = 50, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.mlHistory,
    queryFn: () => estimationService.getPredictionHistory(limit),
    ...options
  });
};

// Resource Planning Hooks

export const useCheckResourceAvailability = (
  options?: MutOpts<{
    estimationId: string;
    requestedStartDate: string;
    estimation: unknown;
  }>
) => {
  return useMutation({
    mutationFn: ({ estimationId, requestedStartDate, estimation }: { 
      estimationId: string; 
      requestedStartDate: string; 
      estimation: any 
    }) =>
      estimationService.checkResourceAvailability(estimationId, requestedStartDate, estimation),
    ...options
  });
};

export const useEstimateLeadTime = (
  options?: MutOpts<{
    estimationId: string;
    requestedDate: string;
    estimation: unknown;
  }>
) => {
  return useMutation({
    mutationFn: ({ estimationId, requestedDate, estimation }: { 
      estimationId: string; 
      requestedDate: string; 
      estimation: any 
    }) =>
      estimationService.estimateLeadTime(estimationId, requestedDate, estimation),
    ...options
  });
};

export const useResourceForecast = (timeHorizonDays: number = 30, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: estimationKeys.resourceForecast,
    queryFn: () => estimationService.getResourceForecast(timeHorizonDays),
    ...options
  });
};

// BOM Integration Hooks

export const useBOMCostStructure = (bomId: string, options?: UseQueryOptions) => {
  return useQuery({
    queryKey: ['bom-cost-structure', bomId],
    queryFn: () => estimationService.getBOMCostStructure(bomId),
    enabled: !!bomId,
    ...options
  });
};

export const useCalculateBOMCostRollup = (
  options?: MutOpts<{ bomId: string; quantity: number; includeScrap?: boolean }>
) => {
  return useMutation({
    mutationFn: ({ bomId, quantity, includeScrap }: { bomId: string; quantity: number; includeScrap?: boolean }) =>
      estimationService.calculateBOMCostRollup(bomId, quantity, includeScrap),
    ...options
  });
};

